import { useEffect, useState } from 'react';

import { useEngineBus } from '../hooks/useEngineBus.js';
import Panel from '../components/Panel.jsx';

const FLAGS = [
  { key: 'wireframe', label: 'wireframe' },
  { key: 'colliders', label: 'colliders' },
  { key: 'probes', label: 'light probes' },
  { key: 'decalsOnly', label: 'decals only' },
];

function isDebugQuery() {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('debug') === '1';
}

function fmt(n) {
  return typeof n === 'number' ? n.toFixed(2) : '—';
}

/**
 * @file Hidden unless `?debug=1`. Every checkbox only emits a request
 * (`'debug:flag'`) — nothing here renders wireframes/colliders/probes
 * itself; those consumers land with the systems that own that geometry.
 * The camera readout is read-only, fed by `'camera:yaw'` if/when some
 * system starts emitting it (T5's CameraRig, most likely).
 */
export default function DebugPanel({ engine }) {
  const [enabled] = useState(isDebugQuery);
  const [flags, setFlags] = useState({});
  // Only subscribe when the panel can actually be shown — no point paying
  // for a bus subscription that never renders anything.
  const camera = useEngineBus(enabled ? engine : null, 'camera:yaw', { throttle: 150 });

  // The speech read-out. `systems/Speech.js` publishes NOTHING on the bus (it is
  // a per-frame rebuild from manager state, so there is no event to subscribe
  // to), which is why this is a 2 Hz POLL of the gameplay system's `stats()`
  // rather than a `useEngineBus` — the same trade `PerfBadge` makes for
  // `renderer.info`. The system is reached through `engine.systems` by name, so
  // the panel needs no new engine surface. Null the whole way down when the
  // `dialogue` option was never injected, and the row below simply never
  // renders: the panel is byte-identical to what it was without it.
  const [speech, setSpeech] = useState(null);
  useEffect(() => {
    if (!enabled || !engine) return undefined;
    const read = () => {
      const systems = engine.systems;
      const gameplay = Array.isArray(systems) ? systems.find((s) => s?.name === 'gameplay') : null;
      const st = gameplay?.stats?.() ?? null;
      setSpeech(
        st?.speech
          ? {
            live: st.speech.live ?? 0,
            max: st.speech.max ?? 0,
            repaints: st.bubbles?.repaints ?? 0,
          }
          : null,
      );
    };
    read();
    const interval = setInterval(read, 500);
    return () => clearInterval(interval);
  }, [enabled, engine]);

  if (!enabled) return null;

  const toggle = (key) => {
    const on = !flags[key];
    setFlags((f) => ({ ...f, [key]: on }));
    engine?.bus?.emit('debug:flag', { key, on });
  };

  return (
    <Panel className="pointer-events-auto absolute left-3 top-20 w-44 p-3 font-mono text-[10px] text-hud-dim">
      <div className="mb-2 font-hud uppercase tracking-[0.24em] text-hud-faint">debug</div>
      <div className="flex flex-col gap-1.5">
        {FLAGS.map((f) => (
          <label key={f.key} className="flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={!!flags[f.key]}
              onMouseDown={(e) => e.preventDefault()}
              onChange={() => toggle(f.key)}
              className="h-3 w-3 accent-[#ffc10a]"
            />
            {f.label}
          </label>
        ))}
      </div>

      {speech && (
        <div className="mt-2 border-t border-hairline pt-2 leading-4">
          <div>
            speech {speech.live}/{speech.max} · repaints {speech.repaints}
          </div>
        </div>
      )}

      {camera != null && (
        <div className="mt-2 border-t border-hairline pt-2 leading-4">
          {typeof camera === 'number' ? (
            <div>yaw {fmt(camera)}</div>
          ) : (
            <>
              {camera.yaw != null && <div>yaw {fmt(camera.yaw)}</div>}
              {camera.pitch != null && <div>pitch {fmt(camera.pitch)}</div>}
              {camera.target && (
                <div>
                  target {fmt(camera.target.x)}, {fmt(camera.target.y)}, {fmt(camera.target.z)}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </Panel>
  );
}
